/**
 * ============================================================================
 * SystemLog 模块 - 验证规则定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义前端查询参数验证规则
 * - 系统日志是只读模块，无表单验证
 * - 主要验证查询参数的格式和范围
 * 
 * 📖 对应后端：
 * - Controller: backend/app/Http/Controllers/Api/V1/LogController.php
 * - 注意：此模块无 FormRequest 文件（只读模块）
 * 
 * ⚠️ 注意事项：
 * - 字段名必须使用 snake_case（与 queries.ts 一致）⚠️⚠️⚠️
 * - 验证规则主要针对查询参数
 * - 日期格式：YYYY-MM-DD
 * - 数值范围：page >= 1, per_page >= 1
 * 
 * 🔄 更新时机：
 * - 查询参数验证规则变化时
 * - 新增查询参数时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取 queries.ts，确认所有查询参数都有对应的验证规则
 * 2. 检查验证规则的合理性
 * 
 * @module types/system-log/validations
 * @generated Manual (AI-assisted)
 * @last-updated 2025-10-18
 * ============================================================================
 */

import type { 
  LoginLogQueryParams, 
  LoginStatsQueryParams, 
  LoginTrendQueryParams,
  SuspiciousLogsQueryParams 
} from './queries'
import { StatisticsPeriod, TrendGroupBy } from './enums'

/**
 * ⚠️ ValidationRule 已移至 constants/common.ts
 * 
 * 从全局常量模块导入验证规则接口
 * @see constants/common.ts
 */
import type { ValidationRule } from '../../constants/common'

/**
 * 登录日志列表查询参数验证规则
 */
export const LOGIN_LOG_QUERY_RULES: Record<keyof LoginLogQueryParams, ValidationRule> = {
  page: {
    min: 1,
    message: '页码必须大于等于1',
  },
  per_page: {
    min: 1,
    max: 100,
    message: '每页数量必须在1-100之间',
  },
  username: {
    minLength: 1,
    maxLength: 255,
    message: '用户名长度必须在1-255之间',
  },
  ip_address: {
    pattern: /^(\d{1,3}\.){3}\d{1,3}$/,
    message: 'IP地址格式不正确',
  },
  is_successful: {
    message: '是否成功必须是布尔值',
  },
  device_name: {
    maxLength: 255,
    message: '设备名称长度不能超过255',
  },
  start_date: {
    pattern: /^\d{4}-\d{2}-\d{2}$/,
    message: '开始日期格式必须为YYYY-MM-DD',
  },
  end_date: {
    pattern: /^\d{4}-\d{2}-\d{2}$/,
    message: '结束日期格式必须为YYYY-MM-DD',
  },
}

/**
 * 登录统计查询参数验证规则
 */
export const LOGIN_STATS_QUERY_RULES: Record<keyof LoginStatsQueryParams, ValidationRule> = {
  period: {
    enum: Object.values(StatisticsPeriod),
    message: '统计周期必须是: today, week, month, year 之一',
  },
}

/**
 * 登录趋势查询参数验证规则
 */
export const LOGIN_TREND_QUERY_RULES: Record<keyof LoginTrendQueryParams, ValidationRule> = {
  period: {
    enum: Object.values(StatisticsPeriod),
    message: '统计周期必须是: today, week, month, year 之一',
  },
  groupBy: {
    enum: Object.values(TrendGroupBy),
    message: '分组方式必须是: hour, day 之一',
  },
}

/**
 * 异常登录查询参数验证规则
 */
export const SUSPICIOUS_LOGS_QUERY_RULES: Record<keyof SuspiciousLogsQueryParams, ValidationRule> = {
  hours: {
    min: 1,
    max: 168, // 最多查询7天（168小时）
    message: '查询时长必须在1-168小时之间',
  },
}

/**
 * 验证日期范围（结束日期不能早于开始日期）
 */
export function validateDateRange(startDate?: string, endDate?: string): boolean {
  if (!startDate || !endDate) {
    return true
  }
  
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  return start <= end
}

/**
 * 验证日期格式（YYYY-MM-DD）
 */
export function validateDateFormat(date: string): boolean {
  const pattern = /^\d{4}-\d{2}-\d{2}$/
  if (!pattern.test(date)) {
    return false
  }
  
  // 验证是否是有效日期
  const parts = date.split('-').map(Number)
  const year = parts[0]!
  const month = parts[1]!
  const day = parts[2]!
  const dateObj = new Date(year, month - 1, day)
  
  return (
    dateObj.getFullYear() === year &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getDate() === day
  )
}

/**
 * 验证IP地址格式
 */
export function validateIpAddress(ip: string): boolean {
  const pattern = /^(\d{1,3}\.){3}\d{1,3}$/
  if (!pattern.test(ip)) {
    return false
  }
  
  // 验证每个段是否在0-255之间
  const segments = ip.split('.').map(Number)
  return segments.every(segment => segment >= 0 && segment <= 255)
}

